import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';
import {
  MultiValueGenericProps,
  OptionProps,
  PlaceholderProps,
  components,
} from 'react-select';
import AsyncSelect from 'react-select/async';

import { useApiClient } from '@eluve/api-client-provider';
import { MedicineDetails } from '@eluve/blocks';
import { Box, HStack, Icon, Input, VStack } from '@eluve/components';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import {
  EmotionCacheProvider,
  MultiValueRemove,
  searchableStyle,
} from '../Searchable.styles';

export type Medication = {
  id: string;
  name: string;
  ingredients?: string;
  externalMedicineId?: string;
  rawData?: Record<string, unknown>;
};

interface SearchableMedicinesProps {
  disabled?: boolean;
  onItemAdded?: (option: Medication) => void | Promise<void>;
  onItemRemoved?: (option: Medication) => void | Promise<void>;
  selectedItems?: Medication[];
  shouldRenderValue?: boolean;
}

const Placeholder = (props: PlaceholderProps<Medication>) => {
  return (
    <HStack className="col-start-1 col-end-3 row-start-1 row-end-2 inline-flex gap-3 pl-2">
      <Icon size="xs" name="Search" />
      <components.Placeholder {...props} />
    </HStack>
  );
};

const MultiValueLabel = (_props: MultiValueGenericProps<Medication>) => {
  return null;
};

const MultiValueContainer = (props: MultiValueGenericProps<Medication>) => {
  const { data, selectProps } = props;
  const medicine = data as Medication;

  return (
    <MedicineDetails
      name={medicine.name}
      ingredients={medicine.ingredients ?? ''}
      endAdornment={
        selectProps?.isDisabled ? null : (
          <components.MultiValueRemove {...props} />
        )
      }
    />
  );
};

const Option = (props: OptionProps<Medication>) => {
  const { data } = props;
  const medication = data as Medication;

  return (
    <components.Option {...props} className="flex items-center gap-3">
      <div>{medication.name}</div>
    </components.Option>
  );
};

export const SearchableMedicines: React.FC<SearchableMedicinesProps> = ({
  disabled,
  onItemAdded,
  onItemRemoved,
  selectedItems,
  shouldRenderValue,
}) => {
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams()!;
  const useFdaDrugs = useIsPostHogFeatureFlagEnabled('USE_FDA_DRUGS');

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Medication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const searchMedicineBase = useCallback(
    async (keyword: string): Promise<Medication[]> => {
      if (!keyword.trim()) return [];

      try {
        const result = await apiClient.medicine.searchMedicineInventory({
          query: { keyword: keyword.trim() },
          params: { tenantId },
        });

        if (result.status === 200) {
          return result.body.filter(
            (searchResult) =>
              !selectedItems?.find(
                (selected) =>
                  selected.id === searchResult.id ||
                  selected.externalMedicineId === searchResult.id,
              ),
          );
        }
      } catch {
        // Error handled silently
      }
      return [];
    },
    [apiClient, tenantId, selectedItems],
  );

  const _searchMedicineInventory = useCallback(
    (keyword: string, callback: (meds: Medication[]) => void) => {
      searchMedicineBase(keyword).then(callback);
    },
    [searchMedicineBase],
  );

  const searchMedicineInventory = useMemo(
    () => debounce(_searchMedicineInventory, 500),
    [_searchMedicineInventory],
  );

  const handleEnterSearch = useCallback(async () => {
    if (!searchTerm.trim() || isLoading) return;

    setIsLoading(true);
    setShowResults(false);

    try {
      const results = await searchMedicineBase(searchTerm);
      setSearchResults(results);
      setShowResults(true);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, searchMedicineBase, isLoading]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleEnterSearch();
      }
      if (event.key === 'Escape') {
        setShowResults(false);
        setSearchTerm('');
      }
    },
    [handleEnterSearch],
  );

  const handleMedicineSelect = useCallback(
    async (medication: Medication) => {
      await onItemAdded?.(medication);
      setShowResults(false);
      setSearchTerm('');
      setSearchResults([]);
    },
    [onItemAdded],
  );

  if (useFdaDrugs) {
    return (
      <Box className="relative w-full">
        {selectedItems && selectedItems.length > 0 && (
          <Box className="mb-3 flex flex-wrap gap-2">
            {selectedItems.map((medication) => (
              <MedicineDetails
                key={medication.id}
                name={medication.name}
                ingredients={medication.ingredients ?? ''}
                endAdornment={
                  disabled ? null : (
                    <button
                      onClick={() => onItemRemoved?.(medication)}
                      className="size-8 rounded-full border-8 border-transparent bg-text-tertiary bg-clip-padding p-0.5 text-bg-secondary"
                    >
                      <Icon className="size-3" name="X" size="xs" />
                    </button>
                  )
                }
              />
            ))}
          </Box>
        )}

        <Box className="relative">
          <Box className="relative">
            <Icon
              size="xs"
              name="Search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
            />
            <Input
              disabled={disabled}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type medicine name and press Enter to search"
              className="pl-10"
            />
          </Box>

          {isLoading && (
            <Box className="absolute right-3 top-1/2 -translate-y-1/2">
              <Box className="size-4 animate-spin rounded-full border-2 border-border-primary border-t-transparent" />
            </Box>
          )}
        </Box>

        {showResults && (
          <Box className="absolute z-[9999] mt-3 w-full rounded-2xl border border-border-primary bg-white p-3 shadow-xl">
            {searchResults.length > 0 ? (
              <VStack gap={1}>
                {searchResults.map((medication) => (
                  <button
                    key={medication.id}
                    onClick={() => handleMedicineSelect(medication)}
                    className="w-full rounded-lg p-3 text-left hover:bg-border-primary"
                  >
                    {medication.name}
                  </button>
                ))}
              </VStack>
            ) : (
              <Box className="p-3 text-text-tertiary">
                {searchTerm
                  ? `No results for "${searchTerm}"`
                  : 'No medicines found'}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <EmotionCacheProvider>
      <AsyncSelect<Medication, true>
        isDisabled={disabled}
        controlShouldRenderValue={shouldRenderValue}
        backspaceRemovesValue={!shouldRenderValue}
        placeholder="Choose medicine you would like to add"
        noOptionsMessage={({ inputValue }) =>
          inputValue
            ? `No results for "${inputValue}"`
            : 'Search medicine inventory'
        }
        loadingMessage={() => 'Searching...'}
        isMulti
        value={selectedItems}
        isClearable={false}
        classNames={{
          container: () => searchableStyle.container(),
          control: ({ isDisabled }) =>
            searchableStyle.control({ disabled: isDisabled }),
          valueContainer: () => searchableStyle.valueContainer(),
          menu: () => searchableStyle.menu(),
          menuList: () => searchableStyle.menuList(),
          placeholder: () => searchableStyle.placeholder(),
          multiValue: () => searchableStyle.multiValue(),
          indicatorSeparator: () => searchableStyle.indicatorSeparator(),
          dropdownIndicator: () => searchableStyle.dropdownIndicator(),
          loadingIndicator: () => searchableStyle.loadingIndicator(),
          indicatorsContainer: () => searchableStyle.indicatorsContainer(),
          multiValueRemove: () => searchableStyle.multiValueRemove(),
          option: ({ isFocused }) =>
            searchableStyle.option({ optionFocused: isFocused }),
          noOptionsMessage: () => searchableStyle.noOptionsMessage(),
        }}
        onChange={async (input) => {
          const items = selectedItems ?? [];
          const removed = items.filter((f) => !input.includes(f));
          const added = input.filter((f) => !items.includes(f));

          if (removed.length) {
            const [toRemove] = removed;
            await onItemRemoved?.(toRemove!);
          }

          if (added.length) {
            const [toAdd] = added;
            await onItemAdded?.(toAdd!);
          }
        }}
        getOptionValue={(option) => option.id}
        getOptionLabel={(option) => option.name}
        components={{
          MultiValueContainer,
          MultiValueLabel,
          MultiValueRemove,
          Option,
          Placeholder,
        }}
        loadOptions={searchMedicineInventory}
      />
    </EmotionCacheProvider>
  );
};
